'use client'

import { Amplify } from 'aws-amplify'
import { fetchAuthSession } from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { AUTH_STORAGE_KEYS } from '@/shared/constants/authStorageConstants'
import { cognitoConfig } from '@/shared/constants/authEnvConstants'

let isAmplifyConfigured = false

const customTokenStorage = {
  getItem: async (key: string): Promise<string | null> => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(key)
  },
  setItem: async (key: string, value: string): Promise<void> => {
    if (typeof window === 'undefined') return
    localStorage.setItem(key, value)
  },
  removeItem: async (key: string): Promise<void> => {
    if (typeof window === 'undefined') return
    localStorage.removeItem(key)
  },
  clear: async (): Promise<void> => {
    if (typeof window === 'undefined') return
    localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken)
  },
}

export function configureAmplify(): boolean {
  if (isAmplifyConfigured) {
    return true
  }

  if (typeof window === 'undefined') {
    return false
  }

  if (!cognitoConfig.userPoolId || !cognitoConfig.clientId) {
    console.warn(
      'Amplify: Missing NEXT_PUBLIC_COGNITO_USER_POOL_ID or NEXT_PUBLIC_COGNITO_CLIENT_ID. Token refresh via Amplify will not work.',
    )
    return false
  }

  try {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: cognitoConfig.userPoolId,
          userPoolClientId: cognitoConfig.clientId,
        },
      },
    })

    cognitoUserPoolsTokenProvider.setKeyValueStorage(customTokenStorage)
    isAmplifyConfigured = true
    return true
  } catch (error) {
    console.error('Failed to configure Amplify:', error)
    return false
  }
}

export function isAmplifyReady(): boolean {
  return isAmplifyConfigured
}

export async function refreshTokensWithAmplify(): Promise<{
  accessToken: string
  idToken: string
} | null> {
  if (!isAmplifyConfigured) {
    return null
  }

  try {
    const session = await fetchAuthSession({ forceRefresh: true })

    if (!session.tokens?.accessToken || !session.tokens.idToken) {
      return null
    }

    return {
      accessToken: session.tokens.accessToken.toString(),
      idToken: session.tokens.idToken.toString(),
    }
  } catch (error) {
    console.error('Amplify token refresh error:', error)
    return null
  }
}
